import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ShoppingCart, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShopifyProduct } from '@/services/shopify';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ProductQuickViewProps {
  product: ShopifyProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ProductQuickView: React.FC<ProductQuickViewProps> = ({
  product,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const addItem = useCartStore(state => state.addItem);
  const { formatPrice } = useCurrencyStore();
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');
  const [selectedImage, setSelectedImage] = useState(0);

  if (!product) return null;

  const { node } = product;
  const variants = node.variants.edges;
  const images = node.images.edges;
  const options = node.options;

  // Set default variant if not selected
  React.useEffect(() => {
    if (variants.length > 0 && !selectedVariantId) {
      setSelectedVariantId(variants[0].node.id);
    }
  }, [variants, selectedVariantId]);

  const selectedVariant = variants.find(v => v.node.id === selectedVariantId)?.node || variants[0]?.node;

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast({
        title: "Product unavailable",
        description: "This product is currently unavailable.",
        variant: "destructive",
      });
      return;
    }

    const cartItem = {
      product,
      variantId: selectedVariant.id,
      variantTitle: selectedVariant.title,
      price: selectedVariant.price,
      quantity: 1,
      selectedOptions: selectedVariant.selectedOptions || []
    };
    
    addItem(cartItem);
    
    toast({
      title: "Added to cart",
      description: `${node.title} has been added to your cart.`,
    });
    
    onClose();
  };

  const handleViewFullDetails = () => {
    navigate(`/product/${node.handle}`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">Quick View: {node.title}</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="w-full bg-secondary/10 rounded-lg overflow-hidden">
              {images[selectedImage]?.node ? (
                <img
                  src={images[selectedImage].node.url}
                  alt={images[selectedImage].node.altText || node.title}
                  className="w-full h-auto object-contain"
                />
              ) : (
                <div className="w-full aspect-square flex items-center justify-center">
                  <span className="text-muted-foreground">No image</span>
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={cn(
                      "flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors",
                      selectedImage === index
                        ? "border-primary"
                        : "border-transparent hover:border-border"
                    )}
                  >
                    <img
                      src={image.node.url}
                      alt={image.node.altText || `${node.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold mb-2">{node.title}</h2>
              <p className="text-2xl font-bold text-primary">
                {selectedVariant && formatPrice(
                  parseFloat(selectedVariant.price.amount),
                  selectedVariant.price.currencyCode
                )}
              </p>
            </div>

            {/* Availability */}
            {selectedVariant && (
              <div>
                {selectedVariant.availableForSale ? (
                  <Badge variant="outline" className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20">
                    In Stock
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20">
                    Out of Stock
                  </Badge>
                )}
              </div>
            )}

            {/* Description */}
            {node.description && (
              <div>
                <p className="text-muted-foreground line-clamp-4">
                  {node.description}
                </p>
              </div>
            )}

            {/* Variant Selection */}
            {options.length > 0 && options[0].values.length > 1 && (
              <div className="space-y-3">
                {options.map((option) => (
                  <div key={option.name}>
                    <label className="text-sm font-medium mb-2 block">
                      {option.name}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {option.values.map((value) => {
                        const variant = variants.find(v =>
                          v.node.selectedOptions.some(
                            opt => opt.name === option.name && opt.value === value
                          )
                        );
                        const isSelected = selectedVariant?.selectedOptions.some(
                          opt => opt.name === option.name && opt.value === value
                        );
                        const isAvailable = variant?.node.availableForSale;

                        return (
                          <Button
                            key={value}
                            variant={isSelected ? "default" : "outline"}
                            size="sm"
                            disabled={!isAvailable}
                            onClick={() => {
                              if (variant) {
                                setSelectedVariantId(variant.node.id);
                              }
                            }}
                            className={cn(
                              !isAvailable && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {value}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 pt-4">
              <Button
                onClick={handleAddToCart}
                className="w-full"
                size="lg"
                disabled={!selectedVariant?.availableForSale}
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
              
              <Button
                onClick={handleViewFullDetails}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Full Details
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
